import React, { useState, useEffect, useCallback, useContext, } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, Grid, IconButton, InputLabel, Menu, MenuItem, Select, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DialogTransition from '../DialogTransition/DialogTansition';
import { FetchContext } from '../../features/contexts/fetch';
import { AxiosInstance } from 'axios';
import { useDispatch } from 'react-redux';
import { TNtfyNew } from '../../_types/ntfy';
import { NtfyReport } from '../../features/store/ntfy.slice';

export interface FetchContextType {
  authAxios: any;
  isAdmin: boolean;
  canAdd: boolean;
  canManageAdasSubscriptions: boolean;
  canReadAll: boolean;
  canCreateSecurityCodes: boolean;
  canManageUsers: boolean;
  canManageAdasPackages: boolean;
  canManageAdasTargets: boolean;
  canManageIsoRigs: boolean;
}

// Add Target Dialog
const AddTargetDialog = ({ open, onClose, onSave, packages }: {
  open: boolean; onClose: () => void; onSave: (newTarget: any) => Promise<void>; packages: any[];}) => {

  const [form, setForm] = useState({
    targetName: '',
    featureNumber: 0,
    subscriptionId: '' as string | number,
    packageName: '',
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        targetName: '',
        featureNumber: 0,
        subscriptionId: '',
        packageName: '',
      });
    }
  }, [open]);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.targetName || !form.subscriptionId) return;

    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New ADAS Target</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Target Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField label="Target Name" size="small" fullWidth value={form.targetName}
              onChange={(e) => handleChange('targetName', e.target.value)} />
          </Grid>

          <Grid item xs={6}>
            <TextField label="Feature Number" size="small" type="number" fullWidth value={form.featureNumber}
              onChange={(e) => handleChange('featureNumber', Number(e.target.value))} />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Package Assignment
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Package</InputLabel>
              <Select label="Select Package" value={form.subscriptionId}
                onChange={(e) => { const id = Number(e.target.value);
                  const pkg = packages.find((p) => p.subscriptionId === id);
                  if (pkg) {
                    setForm({
                      ...form,
                      subscriptionId: id,
                      packageName: pkg.packageName
                    });
                  }
                }}>
                {packages.map((pkg) => (
                  <MenuItem key={pkg.subscriptionId} value={pkg.subscriptionId}>
                    {pkg.packageName} (ID: {pkg.subscriptionId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" disabled={saving || !form.targetName || !form.subscriptionId}
          onClick={handleSubmit}>
          {saving ? <CircularProgress size={24} /> : 'Create Target'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete Target Dialog
const DeleteTargetDialog = ({ open, target, onClose, onDelete }: {
  open: boolean; target: any | null; onClose: () => void; onDelete: () => void;}) => {

  const [countdown, setCountdown] = useState(5);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!open) return;

    setCountdown(5);
    setChecked(false);

    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  if (!target) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete ADAS Target</DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You are about to permanently delete this ADAS Target:
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Target Name: {target.targetName}
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            Feature Number: {target.featureNumber}
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            Package Name: {target.packageName}
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            Subscription ID: {target.subscriptionId}
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            Record ID: {target.id}
          </Typography>
        </Box>

        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          This action is immediate and cannot be undone.
        </Typography>

        <FormControlLabel
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label="I understand that this is immediate and permanent"
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" color="error" disabled={countdown > 0 || !checked} onClick={onDelete}>
          {countdown > 0 ? `Delete (${countdown})` : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Target Dialog
const EditTargetDialog = ({ open, target, packages, onClose, onSave }: {
  open: boolean; target: any | null; packages: any[]; onClose: () => void;
  onSave: (updated: any) => Promise<void>;}) => {

  const [form, setForm] = useState<{
    id: number;
    targetName: string;
    featureNumber: number;
    subscriptionId: number | string;
    packageName: string;
  } | null>(null);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target && open) {
      setForm({
        id: target.id,
        targetName: target.targetName,
        featureNumber: target.featureNumber,
        subscriptionId: target.subscriptionId,
        packageName: target.packageName,
      });
    }
  }, [target, open]);

  if (!form) return null;

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...(prev as any), [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit ADAS Target</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Target Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField label="Target Name" size="small" fullWidth value={form.targetName}
              onChange={(e) => handleChange('targetName', e.target.value)} />
          </Grid>

          <Grid item xs={6}>
            <TextField label="Feature Number" size="small" type="number" fullWidth value={form.featureNumber}
              onChange={(e) => handleChange('featureNumber', Number(e.target.value))} />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Package Assignment
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Subscription ID</InputLabel>
              <Select label="Select Package" value={form.subscriptionId}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const selectedPkg = packages.find((p) => p.subscriptionId === id);
                  if (selectedPkg) {
                    setForm({
                      ...form,
                      subscriptionId: id,
                      packageName: selectedPkg.packageName,
                    });
                  }
                }}>
                {packages.map((pkg) => (
                  <MenuItem key={pkg.subscriptionId} value={pkg.subscriptionId}>
                    {pkg.packageName} (ID: {pkg.subscriptionId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" disabled={saving} onClick={handleSubmit}>
          {saving ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Package Dialog
const EditPackageDialog = ({ open, pkg, onClose, onSave }: {
  open: boolean; pkg: any | null; onClose: () => void; onSave: (updated: any) => Promise<void>;}) => {

  const [form, setForm] = useState<{
    subscriptionId: number;
    packageName: string;
  } | null>(null);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (pkg && open) {
      setForm({
        subscriptionId: pkg.subscriptionId,
        packageName: pkg.packageName,
      });
    }
  }, [pkg, open]);

  if (!form) return null;

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...(prev as any), [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Package</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
          Package Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Subscription ID" size="small" type="number" fullWidth value={form.subscriptionId}
              onChange={(e) => handleChange('subscriptionId', Number(e.target.value))} />
          </Grid>

          <Grid item xs={6}>
            <TextField label="Package Name" size="small" fullWidth value={form.packageName}
              onChange={(e) => handleChange('packageName', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" disabled={saving} onClick={handleSubmit}>
          {saving ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete Package Dialog
const DeletePackageDialog = ({ open, pkg, onClose, onDelete }: {
  open: boolean; pkg: any | null; onClose: () => void; onDelete: () => void;}) => {

  const [countdown, setCountdown] = useState(5);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!open) return;

    setCountdown(5);
    setChecked(false);

    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  if (!pkg) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Package</DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You are about to permanently delete this ADAS Package:
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Subscription ID: {pkg.subscriptionId}
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            Package Name: {pkg.packageName}
          </Typography>
        </Box>

        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          This action is immediate and cannot be undone.
        </Typography>

        <FormControlLabel
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label="I understand that this is immediate and permanent"
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" color="error" disabled={countdown > 0 || !checked} onClick={onDelete}>
          {countdown > 0 ? `Delete (${countdown})` : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Component
const ManageAdasSubscriptionsDialog = ({ open, onClose }: {
  open: boolean;
  onClose: () => void;
}) => {
  const dispatch = useDispatch<any>();
  const fetchContext = useContext(FetchContext) as {
    authAxios?: AxiosInstance | null;
    isAdmin: boolean;
    canAdd: boolean;
    canCreateSecurityCodes: boolean;
    canManageUsers: boolean;
    canManageAdasPackages: boolean;
    canManageAdasTargets: boolean;
  };

  const [targets, setTargets] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedTarget, setSelectedTarget] = useState<any | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<any | null>(null);

  const [openAddTarget, setOpenAddTarget] = useState(false);
  const [openEditTarget, setOpenEditTarget] = useState(false);
  const [openDeleteTarget, setOpenDeleteTarget] = useState(false);

  const [openModifyPackages, setOpenModifyPackages] = useState(false);
  const [openEditPackage, setOpenEditPackage] = useState(false);
  const [openDeletePackage, setOpenDeletePackage] = useState(false);

  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const validatePackagePair = (
    packageName: string,
    subscriptionId: string | number
  ): { ok: boolean; message?: string } => {
    const subIdStr = String(subscriptionId).trim();
    const pkgNameTrimmed = packageName.trim();

    const nameToId = new Map<string, string>();
    const idToName = new Map<string, string>();

    packages.forEach((p) => {
      nameToId.set(p.packageName.trim(), String(p.subscriptionId));
      idToName.set(String(p.subscriptionId), p.packageName.trim());
    });

    const existingId = nameToId.get(pkgNameTrimmed);
    const existingName = idToName.get(subIdStr);

    if (existingId && existingId !== subIdStr) {
      return {
        ok: false,
        message:
          `Package "${pkgNameTrimmed}" already exists with ` +
          `Subscription ID "${existingId}", but you entered "${subIdStr}".`,
      };
    }

    if (existingName && existingName !== pkgNameTrimmed) {
      return {
        ok: false,
        message:
          `Subscription ID "${subIdStr}" already exists with ` +
          `package "${existingName}", but you entered "${pkgNameTrimmed}".`,
      };
    }

    return { ok: true };
  };

  const getAllTargets = useCallback(
    async (normalizedPackages: any[]) => {
      const allTargets: any[] = [];

      for (const pkg of normalizedPackages) {
        if (!fetchContext.authAxios) {
          throw new Error('authAxios unavailable while loading targets');
        }

        const res = await fetchContext.authAxios.post(
          '/Modules/AlignerUtilities.aspx/GetTargetSubscriptionDataByName',
          { sPackageName: pkg.packageName }
        );

        const payload = res.data?.d ?? res.data?.content ?? res.data;
        const targetData = JSON.parse(payload);

        const normalizedTargets = targetData.map((t: any) => ({
          id: Number(t.id),
          targetName: t.TargetName,
          featureNumber: Number(t.Target0_id),
          subscriptionId: pkg.subscriptionId,
          packageName: pkg.packageName,
        }));

        allTargets.push(...normalizedTargets);
      }

      return allTargets;
    },
    [fetchContext.authAxios]
  );

  const loadPackagesAndTargets = useCallback(async () => {
    if (!fetchContext.authAxios || !open) return;

    setLoading(true);

    try {
      const { status, data } = await fetchContext.authAxios.post(
        '/Modules/AlignerUtilities.aspx/ListSubscriptionIdAndNames',
        {}
      );

      if (status === 200) {
        const pkgData = JSON.parse(data.d);

        const normalizedPackages = pkgData.map((p: any) => ({
          subscriptionId: Number(p.Subscription_Id),
          packageName: p.Package,
        }));

        setPackages(normalizedPackages);

        if (normalizedPackages.length === 0) {
          setTargets([]);
          setLoading(false);
          return;
        }

        const allTargets = await getAllTargets(normalizedPackages);
        setTargets(allTargets);
      }
    } catch (err) {
      dispatch(NtfyReport(TNtfyNew('Failed to load ADAS data', 'error', 'long')));
    }

    setLoading(false);
  }, [fetchContext.authAxios, open, getAllTargets, dispatch]);

  useEffect(() => {
    if (open) loadPackagesAndTargets();
  }, [open, loadPackagesAndTargets]);

  const handleAddTarget = async (newTarget: any) => {
    const { authAxios } = fetchContext;
    if (!authAxios) return;

    const { targetName, featureNumber, packageName, subscriptionId } = newTarget;

    // Validate packageName <-> subscriptionId pair
    const validation = validatePackagePair(packageName, subscriptionId);
    if (!validation.ok) {
      dispatch(NtfyReport(TNtfyNew(validation.message ?? "Invalid package/ID pair", "error", "long")));
      return;
    }

    try {
      await authAxios.post(
        "/Modules/AlignerUtilities.aspx/CreateTargetSubscriptionAssociation",
        {
          sTargetName: String(targetName),
          sTarget0_id: String(featureNumber),
          sPackageName: String(packageName),
          sSubId: String(subscriptionId),
        },
        { headers: { "Content-Type": "application/json; charset=utf-8" } }
      );

      const updated = [
        ...targets,
        {
          id: crypto.randomUUID(),
          targetName,
          featureNumber,
          packageName,
          subscriptionId,
        },
      ];

      setTargets(updated);
      setOpenAddTarget(false);

      dispatch(NtfyReport(TNtfyNew("Target added successfully", "success", "short")));
    } catch (err) {
      console.error("Add failed:", err);
      dispatch(NtfyReport(TNtfyNew("Failed to add target", "error", "long")));
    }
  };


  const handleSaveTarget = async (updated: any) => {
    const { authAxios } = fetchContext;
    if (!authAxios) return;

    const { id, targetName, featureNumber, subscriptionId, packageName } = updated;

    try {
      await authAxios.post(
        '/Modules/AlignerUtilities.aspx/UpdateTargetInfo',
        {
          sPkID: String(id),
          sNewTargetName: String(targetName),
          sNewFeatureNumber: String(featureNumber),
          sNewPackageName: String(packageName)
        },
        { headers: { 'Content-Type': 'application/json; charset=utf-8' } }
      );

      const newList = targets.map((t) =>
        t.id === id
          ? {
              ...t,
              targetName,
              featureNumber,
              subscriptionId,
              packageName
            }
          : t
      );

      setTargets(newList);
      setOpenEditTarget(false);

      dispatch(NtfyReport(TNtfyNew('Target updated successfully', 'success', 'short')));
    } catch (err) {
      console.error('Update failed:', err);
      dispatch(NtfyReport(TNtfyNew('Failed to update target', 'error', 'long')));
    }
  };

  const handleDeleteTarget = async () => {
    if (!fetchContext.authAxios || !selectedTarget) return;

    try {
      const { status } = await fetchContext.authAxios.post(
        '/Modules/AlignerUtilities.aspx/DeleteTargetFromPackageByID',
        { sManifestSubscriptionRecord_ID: selectedTarget.id }
      );

      if (status !== 200) {
        dispatch(NtfyReport(TNtfyNew('Failed to delete target subscription', 'error', 'long')));
        return;
      }

      const updated = targets.filter((t) => t.id !== selectedTarget.id);
      setTargets(updated);

      setOpenDeleteTarget(false);

      dispatch(NtfyReport(TNtfyNew('Target deleted', 'success', 'short')));
    } catch (err) {
      console.error('Delete failed:', err);
      dispatch(NtfyReport(TNtfyNew('Failed to delete target', 'error', 'long')));
    }
  };

  const handleSavePackage = async (updated: any) => {
    const { authAxios } = fetchContext;
    if (!authAxios) return;

    const {
      id: rowId,
      subscriptionId: newId,
      packageName: newNameRaw,
      oldSubscriptionId,
      oldPackageName
    } = updated;

    const newName = newNameRaw.trim();

    if (!Number.isInteger(newId) || newId <= 0) {
      dispatch(
        NtfyReport(
          TNtfyNew(
            'Subscription ID must be a positive number above zero',
            'error',
            'long'
          )
        )
      );
      return;
    }

    try {
      const { status } = await authAxios.post(
        '/Modules/AlignerUtilities.aspx/UpdateTargetSubscription',
        {
          sPkID: rowId,
          sNewSubscriptionID: newId,
          sNewPackageName: newName
        }
      );

      if (status !== 200) {
        dispatch(NtfyReport(TNtfyNew('Failed to update ADAS package', 'error', 'long')));
        return;
      }

      const newPackages = packages.map((p) =>
        p.packageName === oldPackageName
          ? { subscriptionId: newId, packageName: newName }
          : p
      );
      setPackages(newPackages);

      const newTargets = targets.map((t) =>
        t.subscriptionId === oldSubscriptionId
          ? {
              ...t,
              subscriptionId: newId,
              packageName: newName
            }
          : t
      );
      setTargets(newTargets);

      setOpenEditPackage(false);

      dispatch(
        NtfyReport(TNtfyNew('Package updated successfully', 'success', 'short'))
      );
    } catch (err) {
      console.error('Update failed:', err);
      dispatch(NtfyReport(TNtfyNew('Failed to update package', 'error', 'long')));
    }
  };


  const handleDeletePackage = async () => {
    if (!fetchContext.authAxios || !selectedPackage) return;

    try {
      const { status } = await fetchContext.authAxios.post(
        '/Modules/AlignerUtilities.aspx/DeleteSubscriptionPackageByName',
        { sPackageName: selectedPackage.packageName }
      );

      if (status !== 200) {
        dispatch(NtfyReport(TNtfyNew('Failed to delete ADAS package', 'error', 'long')));
        return;
      }

      // Remove package locally
      const updatedPackages = packages.filter(
        (p) => p.packageName !== selectedPackage.packageName
      );
      setPackages(updatedPackages);

      // Remove all targets belonging to this package
      const updatedTargets = targets.filter(
        (t) => t.packageName !== selectedPackage.packageName
      );
      setTargets(updatedTargets);

      setOpenDeletePackage(false);

      dispatch(NtfyReport(TNtfyNew('Package deleted', 'success', 'short')));
    } catch (err) {
      console.error('Delete failed:', err);
      dispatch(NtfyReport(TNtfyNew('Failed to delete package', 'error', 'long')));
    }
  };

  const renderTargetsTable = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        ADAS Targets
      </Typography>

      <TableContainer sx={{ maxHeight: '40vh' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Target Name</TableCell>
              <TableCell>Feature #</TableCell>
              <TableCell>Package Name</TableCell>
              <TableCell>Subscription ID</TableCell>
              {
                fetchContext.canManageAdasTargets ? (
                  <>
                    <TableCell align="right">Actions</TableCell>
                  </>
                ) : null
              }
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress sx={{ m: 2 }} />
                </TableCell>
              </TableRow>
            ) : targets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No targets found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              targets
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.targetName}</TableCell>
                    <TableCell>{row.featureNumber}</TableCell>
                    <TableCell>{row.packageName}</TableCell>
                    <TableCell>{row.subscriptionId}</TableCell>
                    {
                      fetchContext.canManageAdasTargets ? (
                        <>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Edit Target">
                                <IconButton size="small" color="primary"
                                  onClick={() => { setSelectedTarget(row); setOpenEditTarget(true); }}>
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete Target">
                                <IconButton size="small" color="error"
                                  onClick={() => { setSelectedTarget(row); setOpenDeleteTarget(true); }}>
                                  <DeleteIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </>
                      ) : null
                    }
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderPackagesTable = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        ADAS Packages
      </Typography>

      <TableContainer sx={{ maxHeight: '30vh' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Subscription ID</TableCell>
              <TableCell>Package Name</TableCell>
              {
                fetchContext.canManageAdasPackages ? (
                  <>
                    <TableCell align="right">Actions</TableCell>
                  </>
                ) : null
              }
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress sx={{ m: 2 }} />
                </TableCell>
              </TableRow>
            ) : packages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No packages found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              packages.map((pkg) => (
                <TableRow key={pkg.subscriptionId} hover>
                  <TableCell>{pkg.subscriptionId}</TableCell>
                  <TableCell>{pkg.packageName}</TableCell>
                  {
                    fetchContext.canManageAdasPackages ? (
                      <>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Edit Package">
                              <IconButton size="small" color="primary"
                                onClick={() => { setSelectedPackage(pkg); setOpenEditPackage(true); }}>
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete Package">
                              <IconButton size="small" color="error"
                                onClick={() => { setSelectedPackage(pkg); setOpenDeletePackage(true); }}>
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </>
                    ) : null
                  }
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

    const handleExportCSV = () => {
    const rows = targets.map((t) => ({
      ID: t.id,
      TargetName: t.targetName,
      FeatureNumber: t.featureNumber,
      SubscriptionID: t.subscriptionId,
      PackageName: t.packageName,
    }));

    const header = Object.keys(rows[0] || {}).join(',');
    const body = rows.map((r) => Object.values(r).join(',')).join('\n');
    const csv = `${header}\n${body}`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'adas_targets.csv';
    a.click();

    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const json = JSON.stringify(targets, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'adas_targets.json';
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      TransitionComponent={DialogTransition}>
      <DialogTitle>Manage ADAS Subscriptions</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {renderTargetsTable()}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {
              fetchContext.canManageAdasTargets ? (
                <>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenAddTarget(true)}>
                    Add Target
                  </Button>
                </>
              ) : null
            }
            <Button variant="outlined" startIcon={<SettingsSuggestIcon />} onClick={() => setOpenModifyPackages(true)}>
              Packages
            </Button>
            <Button variant="outlined" startIcon={<FileDownloadIcon />}
              onClick={(e) => setExportAnchor(e.currentTarget)}>
              Export
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}>
              <MenuItem onClick={() => { setExportAnchor(null); handleExportCSV(); }}>
                Export CSV
              </MenuItem>
              <MenuItem onClick={() => { setExportAnchor(null); handleExportJSON(); }}>
                Export JSON
              </MenuItem>
            </Menu>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', }}>
            <TablePagination
              component="div"
              count={targets.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <AddTargetDialog
        open={openAddTarget}
        onClose={() => setOpenAddTarget(false)}
        onSave={handleAddTarget}
        packages={packages}
      />

      <EditTargetDialog
        open={openEditTarget}
        target={selectedTarget}
        packages={packages}
        onClose={() => setOpenEditTarget(false)}
        onSave={handleSaveTarget}
      />

      <DeleteTargetDialog
        open={openDeleteTarget}
        target={selectedTarget}
        onClose={() => setOpenDeleteTarget(false)}
        onDelete={handleDeleteTarget}
      />

      <Dialog
        open={openModifyPackages}
        onClose={() => setOpenModifyPackages(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Modify ADAS Packages</DialogTitle>
        <DialogContent dividers>
          {renderPackagesTable()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModifyPackages(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <EditPackageDialog
        open={openEditPackage}
        pkg={selectedPackage}
        onClose={() => setOpenEditPackage(false)}
        onSave={handleSavePackage}
      />

      <DeletePackageDialog
        open={openDeletePackage}
        pkg={selectedPackage}
        onClose={() => setOpenDeletePackage(false)}
        onDelete={handleDeletePackage}
      />
    </Dialog>
  );
};

export default ManageAdasSubscriptionsDialog;
